// Prequalification funnel — AI guide state shared by the buyer + seller
// pages (§2 live-range/guide behavior).
//
// Semantics:
//  • Step ENTRY fires the tip at most once per step, and NEVER while the
//    step's PRIMARY fields are still empty (the staging bug: tips about
//    still-empty fields). A step whose fields are already filled — e.g.
//    returning via Back — may fire on entry.
//  • A meaningful answer change refreshes the CURRENT step's tip on a
//    ~1.5s debounce (canceled on step change / unmount) and invalidates
//    tips fetched for LATER steps (their payload changed).
//  • The manual "↻ Update tip" affordance forces an immediate refresh.
//  • Failure posture: any failure (incl. 429) or a null response silently
//    keeps the LAST GOOD tip — never an error state, never a retry loop.
// The debounce + entry gating keep call volume far inside the server's
// PrequalLivePolicy (60/min).

import { useEffect, useRef, useState } from "react";
import type { FunnelStep } from "./PrequalBits";

/** Auto-refresh debounce after a field change within the step. */
export const GUIDE_REFRESH_DEBOUNCE_MS = 1500;

interface UsePrequalGuideOptions {
  steps: FunnelStep[];
  stepIndex: number;
  /** Per-step PRIMARY-field predicate — the guide never fires for a step
   *  whose primary fields are still empty. */
  stepReady: (stepKey: string) => boolean;
  /** Fetches the tip for a step (null = AI unavailable; rejection = any
   *  transport failure — both keep the last good tip). */
  fetchTip: (stepKey: string) => Promise<string | null>;
}

export interface PrequalGuide {
  /** Per-step tip map — only the CURRENT step's tip ever renders. */
  guides: Record<string, string | null>;
  /** A tip request is in flight (disables the manual refresh control). */
  refreshing: boolean;
  /** Call from the step-navigation path (goTo). */
  onStepEnter: (stepKey: string) => void;
  /** Call from every answer-change path (set / knockout answer). */
  onAnswerChanged: () => void;
  /** Manual "↻ Update tip". */
  refreshNow: () => void;
  /** "Adjust my answers" / restart — clears tips, fetch guards, timers. */
  reset: () => void;
}

export function usePrequalGuide({
  steps,
  stepIndex,
  stepReady,
  fetchTip,
}: UsePrequalGuideOptions): PrequalGuide {
  const [guides, setGuides] = useState<Record<string, string | null>>({});
  const [refreshing, setRefreshing] = useState(false);
  // At-most-once-per-step ENTRY guard (a failed call never re-fires on
  // re-entry; the panel just keeps whatever it had).
  const fetchedStepsRef = useRef<Set<string>>(new Set());
  const debounceRef = useRef<number | null>(null);
  // Latest-render values so timer callbacks never see stale answers.
  const latestRef = useRef({ steps, stepIndex, stepReady, fetchTip });
  latestRef.current = { steps, stepIndex, stepReady, fetchTip };

  // Cancel any pending auto-refresh on step change and on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [stepIndex]);

  function cancelPending() {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }

  function fire(stepKey: string) {
    fetchedStepsRef.current.add(stepKey);
    setRefreshing(true);
    void latestRef.current
      .fetchTip(stepKey)
      .then((prose) => {
        // Null (AI unavailable) keeps the last good tip.
        if (prose) setGuides((g) => ({ ...g, [stepKey]: prose }));
      })
      .catch(() => {
        // Silent — keep the last good tip; never an error state or refire.
      })
      .finally(() => setRefreshing(false));
  }

  function onStepEnter(stepKey: string) {
    cancelPending();
    if (stepKey === "result") return; // no rail (and no tip) on the ceremony
    if (fetchedStepsRef.current.has(stepKey)) return;
    if (!latestRef.current.stepReady(stepKey)) return;
    fire(stepKey);
  }

  function onAnswerChanged() {
    const { steps: allSteps, stepIndex: idx } = latestRef.current;
    // Invalidate tips fetched for LATER steps — they refetch on next entry.
    const later = allSteps
      .slice(idx + 1)
      .map((s) => s.key)
      .filter((k) => fetchedStepsRef.current.has(k));
    if (later.length > 0) {
      for (const k of later) fetchedStepsRef.current.delete(k);
      setGuides((g) => {
        const next = { ...g };
        for (const k of later) delete next[k];
        return next;
      });
    }
    const stepKey = allSteps[idx].key;
    if (stepKey === "result") return;
    cancelPending();
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      const { steps: s, stepIndex: i, stepReady: ready } = latestRef.current;
      if (s[i].key !== stepKey) return; // step changed while pending (belt)
      if (!ready(stepKey)) return;
      fire(stepKey);
    }, GUIDE_REFRESH_DEBOUNCE_MS);
  }

  function refreshNow() {
    const { steps: s, stepIndex: i, stepReady: ready } = latestRef.current;
    const stepKey = s[i].key;
    if (stepKey === "result" || !ready(stepKey)) return;
    cancelPending();
    fire(stepKey);
  }

  function reset() {
    cancelPending();
    fetchedStepsRef.current.clear();
    setGuides({});
  }

  return { guides, refreshing, onStepEnter, onAnswerChanged, refreshNow, reset };
}
